import React, { useEffect, useState } from "react";
import { Box, Drawer } from "@mui/material";
import { Outlet } from "react-router-dom";
import { Footer } from "../Footer/Footer";
import { MsaBanner } from "../MsaBanner/MsaBanner";
import { TopBar } from "./TopBar";
import { RAIL_WIDTH, RAIL_WIDTH_COLLAPSED, SideNavContent } from "./SideNavContent";
import { useSideNavData } from "./sideNavModel";
import { useDarkThemeScope } from "../../hooks/useDarkThemeScope";

const COLLAPSE_KEY = "tdc_sidenav_collapsed";

// App shell for every authenticated route: a customized MUI Drawer as the
// primary side navigation (permanent + collapsible on desktop, temporary
// full-screen on mobile) with the secondary TopBar and page content in the
// column to its right. Connected treatment: the rail runs flush to the left
// viewport edge at full height with a soft shadow (no hard rule), and the top
// bar sits flush beside it. Replaces the old horizontal HeaderV2 top nav.
// Children override the router Outlet for pages outside the role routers
// (see EdgeShell).
export function AppShell({ children }: { children?: React.ReactNode }) {
    // The authenticated shell is the surface that supports dark mode; public
    // and white-label routes never enter a scope, so they stay light.
    useDarkThemeScope();

    const { sections, workspace, account, helpLink, activePath } = useSideNavData();

    const [collapsed, setCollapsed] = useState<boolean>(() => {
        try {
            return localStorage.getItem(COLLAPSE_KEY) === "1";
        } catch {
            return false;
        }
    });
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
        } catch {
            /* ignore */
        }
    }, [collapsed]);

    const width = collapsed ? RAIL_WIDTH_COLLAPSED : RAIL_WIDTH;

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "var(--page)" }}>
            {/* Desktop — permanent, width-collapsible rail flush to the left edge */}
            <Drawer
                variant="permanent"
                open
                // rail-surface (not .MuiDrawer-paper) is what tokens.ts targets to
                // pin --tint/--accent-text/--sh-2 to their dark values: the rail is
                // navy in BOTH themes. Scoping it to this class keeps any future
                // non-rail Drawer from inheriting a rail-only palette.
                PaperProps={{ className: "rail-surface" }}
                sx={{
                    display: { xs: "none", md: "block" },
                    width,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    transition: (theme) =>
                        theme.transitions.create("width", {
                            duration: 200,
                            easing: "cubic-bezier(.2,.7,.2,1)" // design motion token
                        }),
                    "& .MuiDrawer-paper": {
                        width,
                        boxSizing: "border-box",
                        border: 0, // soft shadow, not a hard rule
                        boxShadow: "var(--rail-shadow)",
                        transition: (theme) =>
                            theme.transitions.create("width", {
                                duration: 200,
                                easing: "cubic-bezier(.2,.7,.2,1)" // design motion token
                            })
                    }
                }}
            >
                <SideNavContent
                    sections={sections}
                    activePath={activePath}
                    workspace={workspace}
                    account={account}
                    collapsed={collapsed}
                    onToggleCollapse={() => setCollapsed((v) => !v)}
                />
            </Drawer>

            {/* Mobile — temporary full-screen overlay */}
            <Drawer
                variant="temporary"
                anchor="left"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{ display: { xs: "block", md: "none" } }}
                PaperProps={{
                    className: "rail-surface",
                    sx: { width: "100vw", height: "100dvh", maxWidth: 440 }
                }}
            >
                <SideNavContent
                    sections={sections}
                    activePath={activePath}
                    workspace={workspace}
                    account={account}
                    collapsed={false}
                    mobile
                    onClose={() => setMobileOpen(false)}
                />
            </Drawer>

            {/* Content column */}
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "100vh"
                }}
            >
                <TopBar onMenu={() => setMobileOpen(true)} helpLink={helpLink} />
                <MsaBanner />
                <Box sx={{ flex: 1, minWidth: 0 }}>{children ?? <Outlet />}</Box>
                <Footer />
            </Box>
        </Box>
    );
}
